import React, { forwardRef, useState, useImperativeHandle, useRef, createRef, useEffect } from "react";
import { Alert, Box, CircularProgress, Grid, Typography, Button, Snackbar } from "@mui/material";
import { IncomeSource } from "../../components/Income/IncomeSource";
import Stack from "@mui/material/Stack";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import { PlaidIncomeLink } from "../PlaidIncomeLink";
import { ReactComponent as IncomeImage } from "../../assets/svgs/provide-income.svg";
import { useInterval } from "../../hooks/useInterval";
import { get, post } from "../../api";
import { useAuth } from "../../hooks/useAuth";
import { MobileModal } from "../MobileModal";
import { Input } from "../Input";
import { AddIncome } from "./AddIncome";
import SelfEmployed from "./SelfEmployed";
import { query } from "../../services/object";

const Income = forwardRef((props, ref) => {
    const label = "Income source";
    const [loadingIncomeDetails, setLoadingIncomeDetails] = useState(false);
    const [plaidUserId, setPlaidUserId] = useState("");
    const [addIncomeDetailsModal, setAddIncomeDetailsModal] = useState(false);
    const [displayBack, setDisplayBack] = useState(false);
    const [recordToEdit, setRecordToEdit] = useState();
    const [selfIncomeRecord, setSelfIncomeRecord] = useState();

    const [openToster, setOpenToster] = useState(false);
    const [tosterVariant, setTosterVariant] = useState("error");
    const [tosterMessage, setTosterMessage] = useState("");

    const [selfEmployedIncomeModal, setSelfEmployedIncomeModal] = useState(false);
    const [selfEmployedIncomeModalInfo, setSelfEmployedIncomeModalInfo] = useState({
        heading: "",
        hidePara: true,
        para: "",
    });

    const { getUserDetailsNotAsync, setUserDetailsNotAsync } = useAuth();
    let userDetails = getUserDetailsNotAsync();

    useInterval(
        async () => {
            const result = await get("plaid/verify_income_status", {
                plaidUserId: plaidUserId,
            });

            if (result.isSuccess && result.data && result.data.incomeStatus === "Success") {
                userDetails.applicant = result.data;
                userDetails.applicant.plaidIncomeVerified = true;
                setUserDetailsNotAsync(userDetails);
                setLoadingIncomeDetails(false);
                window.location.reload();
            }

            if (result.isSuccess && result.data && result.data.incomeStatus === "Failed") {
                userDetails.applicant = result.data;
                userDetails.applicant.plaidIncomeVerified = false;
                setUserDetailsNotAsync(userDetails);
                setLoadingIncomeDetails(false);
            }
        },
        // Passing in the delay parameter. null stops the counter.
        loadingIncomeDetails ? 6000 : null
    );

    const handleIncomeEvent = (event) => {
        if (event.detail.type === "Success") {
            setLoadingIncomeDetails(true);
            setPlaidUserId(event.detail.incomeUserId);
        } else {
            let errorMessage =
                event.detail.type === "Failed"
                    ? `Sorry, we seem to have hit a snag retrieving your payroll information from Plaid. Please enter your income details manually.`
                    : `If you're unable to use Plaid, please enter your income information manually and upload the required documents.`;
            // setTosterVariant("error");
            // setTosterMessage(errorMessage);
            // setOpenToster(true);

            setLoadingIncomeDetails(false);
            setAddIncomeDetailsModal(false);
            setTimeout(() => {
                handleEdit({
                    incomeType: "Traditional Employment",
                    errorMessage: errorMessage,
                    requiredDocument: true,
                });
            });
        }
    };

    const handleIncomeScreenUpdate = (event) => {
        setDisplayBack(event.detail.value);
    };

    const testData = async () => {
        const result = await get("income/test", {
            plaidUserId: plaidUserId,
        });
    };

    useEffect(() => {
        //testData();

        document.addEventListener("incomeEvent", handleIncomeEvent);
        document.addEventListener("income-screen-update", handleIncomeScreenUpdate);
        return () => {
            document.removeEventListener("incomeEvent", handleIncomeEvent);
            document.removeEventListener("income-screen-update", handleIncomeScreenUpdate);
        };
    }, []);

    const [income, setIncome] = useState(
        props.value
            ? props.value
            : [
                  //   {
                  //       incomeType: "",
                  //       employer: "",
                  //       occupation: "",
                  //       employmentStartDate: null,
                  //       annualIncome: "",
                  //   },
              ]
    );

    const incomeSourceRefs = useRef([]);
    incomeSourceRefs.current = income.map((element, i) => incomeSourceRefs.current[i] ?? createRef());

    useImperativeHandle(ref, () => ({
        validate,
    }));

    const validate = () => {
        let hasError = false;
        if (!loadingIncomeDetails && incomeSourceRefs.current?.length > 0) {
            for (let ref of incomeSourceRefs.current) {
                if (!ref.current.validate()) {
                    hasError = true;
                }
            }
        } else {
            hasError = true;
        }

        return !hasError;
    };

    //handle click event of the Add button
    const handleAddClick = () => {
        // let incomeRecords = [
        //     ...income,
        //     {
        //         incomeType: "",
        //         employer: "",
        //         occupation: "",
        //         employmentStartDate: "",
        //         annualIncome: "",
        //     },
        // ];
        // setIncome(incomeRecords);

        setAddIncomeDetailsModal(true);
        setRecordToEdit();
        setDisplayBack(false);
    };

    const handleRemoveClick = async (index) => {
        let incomeRecords = [...income];
        let record = incomeRecords[index];
        if (income.length > 0) {
            if (record.id) {
                get("income/delete", { recordId: record.id });
            }

            incomeRecords.splice(index, 1);
            userDetails.applicant.incomes = incomeRecords;
            setUserDetailsNotAsync(userDetails);
            setIncome(incomeRecords);
            props.onChange(incomeRecords);
        }
    };

    const handleChange = (index, value) => {
        let incomeRecords = [...income];
        incomeRecords[index] = value;
        setIncome(incomeRecords);
        props.onChange(incomeRecords);
    };

    const handleScreenUpdate = (dispalyBack) => {
        setDisplayBack(dispalyBack);
    };

    const handleAddIncomeComplete = (value, editMode) => {
        setAddIncomeDetailsModal(false);
        let incomeRecords;
        if (!editMode) {
            incomeRecords = [...income, value];
        } else {
            let find = false;
            incomeRecords = income.map((item) => {
                if (item.id === value.id) {
                    find = true;
                    return value;
                }
                return item;
            });

            if (!find) {
                incomeRecords = [...income, value];
            }

            setLoadingIncomeDetails(true);
            setTimeout(() => {
                setLoadingIncomeDetails(false);
            }, 10);
        }

        setIncome([...incomeRecords]);
        props.onChange([...incomeRecords]);
    };

    const handleEdit = async (record) => {
        if (record.id) {
            await post("income/getIncomeById", { recordId: record.id }).then(async (result) => {
                if (result.isSuccess) {
                    if (record.incomeType === "Self Employed") {
                        await query("document", "getRelatedRecords", { parentId: record.id }).then((resultDocument) => {
                            if (resultDocument.isSuccess) {
                                let documentData = resultDocument.data?.filter((item) => !item.isDeleted);
                                setRecordToEdit({ ...result.data, s3Documents: documentData });
                                setDisplayBack(false);
                                setAddIncomeDetailsModal(true);
                            }
                        });
                    } else {
                        setRecordToEdit({ ...result.data });
                        setDisplayBack(false);
                        setAddIncomeDetailsModal(true);
                    }
                }
            });
        } else {
            setRecordToEdit({ ...record });
            setDisplayBack(false);
            setAddIncomeDetailsModal(true);
        }
    };

    const handleTosterClose = () => {
        setOpenToster(false);
    };

    const getSelfEmployedHeader = () => {
        const header = (
            <Box display={"flex"} justifyContent={"center"} alignItems={"center"} flexDirection={"column"}>
                <Box
                    sx={{
                        fontWeight: 600,
                        fontSize: "18px",
                        lineHeight: "32px",
                    }}>
                    {selfEmployedIncomeModalInfo.heading || "Heading"}
                </Box>
                {!selfEmployedIncomeModalInfo.hidePara && (
                    <Box
                        sx={{
                            fontWeight: 400,
                            fontSize: "12px",
                            lineHeight: "20px",
                            textAlign: "center",
                            width: "80%",
                        }}>
                        {selfEmployedIncomeModalInfo.para || "subheading"}
                    </Box>
                )}
            </Box>
        );

        return {
            header,
            headerHeight: selfEmployedIncomeModalInfo.hidePara ? 65 : 105,
        };
    };

    return (
        <>
            {/* {userDetails.applicant?.incomeStatus === "Failed" && (
                <Alert severity="error">There is some error while loading the income.</Alert>
            )} */}

            <Snackbar
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
                open={openToster}
                autoHideDuration={6000}
                onClose={handleTosterClose}>
                <Alert onClose={handleTosterClose} severity={tosterVariant} variant="filled">
                    {tosterMessage}
                </Alert>
            </Snackbar>

            {loadingIncomeDetails && (
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                    }}>
                    <CircularProgress
                        color="success"
                        sx={{
                            width: "60px !important",
                            height: "60px !important",
                        }}
                        thickness="3.9"
                    />

                    <Typography variant="h6" component="div" sx={{ mt: "1rem", textAlign: "center" }}>
                        Loading income information...
                    </Typography>
                    <Typography variant="h6" component="div" sx={{ mt: "1rem", textAlign: "center" }}>
                        This may take a minute, please don't go away!
                    </Typography>
                </Box>
            )}

            {!loadingIncomeDetails && income?.length === 0 && (
                <>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            my: "3rem",
                        }}>
                        <IncomeImage style={{ width: "200px" }}></IncomeImage>
                        <Typography variant="h5" component="div" sx={{ mt: "1rem", textAlign: "center" }}>
                            <b>Please provide your income information</b>
                        </Typography>
                        <Typography variant="h6" component="div" sx={{ mt: ".5rem", textAlign: "center" }}>
                            This information is mandatory for your application to process faster.
                        </Typography>
                    </Box>
                </>
            )}
            <Grid container spacing={2}>
                {!loadingIncomeDetails &&
                    income?.length > 0 &&
                    income.map((item, i) => {
                        return (
                            <Grid key={item.id + item.updatedDate} item xs={12} lg={6}>
                                <IncomeSource
                                    ref={incomeSourceRefs.current[i]}
                                    index={i}
                                    value={{ ...item }}
                                    label={label + " " + (i + 1)}
                                    onClick={handleRemoveClick}
                                    onChange={(value) => handleChange(i, value)}
                                    incomeType={item.incomeType}
                                    isEditable={true}
                                    onEdit={handleEdit}
                                    // disabled={
                                    //     item.incomeType === "Traditional Employment" ||
                                    //     item.incomeType === "Payroll"
                                    // }
                                />
                            </Grid>
                        );
                    })}
            </Grid>
            {!loadingIncomeDetails && (
                <Stack direction="row" spacing={2} sx={{ mt: 0, mb: "30px", justifyContent: "center" }}>
                    <Button
                        color="primary"
                        fullWidth
                        variant="contained"
                        sx={{
                            height: "45px",
                            backgroundColor: "#0D2D3A",
                            width: "300px",
                            margin: "auto",
                        }}
                        onClick={() => handleAddClick()}>
                        <AddCircleOutlineOutlinedIcon sx={{ mr: "10px" }} color="white" />
                        Add income source
                    </Button>
                </Stack>
            )}
            <MobileModal
                title="Pathway Homes"
                open={addIncomeDetailsModal}
                onClose={() => {
                    setAddIncomeDetailsModal(false);
                }}
                displayBack={displayBack}>
                <AddIncome
                    income={recordToEdit}
                    editMode={recordToEdit ? true : false}
                    userDetails={userDetails}
                    closeModal={() => setAddIncomeDetailsModal(false)}
                    onAddIncomeComplete={handleAddIncomeComplete}
                    selfEmployedIncomeModalOpen={(income) => {
                        setSelfEmployedIncomeModal(true);
                        setSelfIncomeRecord(income);
                    }}></AddIncome>
            </MobileModal>

            <MobileModal
                open={selfEmployedIncomeModal}
                onClose={() => {
                    setSelfEmployedIncomeModal(false);
                }}
                header={getSelfEmployedHeader()?.header}
                headerHeight={getSelfEmployedHeader()?.headerHeight}
                DialogContentSx={{
                    borderTop: "1px dashed #EAECF0",
                    // borderTopStyle:"dashed"
                }}>
                <SelfEmployed
                    setSelfEmployedIncomeModalInfo={setSelfEmployedIncomeModalInfo}
                    userDetails={userDetails}
                    incomeDetails={recordToEdit ? recordToEdit : selfIncomeRecord}
                    editMode={recordToEdit ? true : false}
                    onAddIncomeComplete={handleAddIncomeComplete}
                    onClose={() => {
                        setSelfEmployedIncomeModal(false);
                    }}
                />
            </MobileModal>
        </>
    );
});
export { Income };
